
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Twitter, Check, ExternalLink } from 'lucide-react';
import { toast } from "sonner";
import { linkTwitterHandleByWallet } from '@/lib/engagement/twitter';

interface TwitterEngagementStepProps {
  onComplete: () => void;
  walletAddress: string;
}

type EngagementStep = 'handle' | 'tweet' | 'verify';

const TwitterEngagementStep = ({ onComplete, walletAddress }: TwitterEngagementStepProps) => {
  const [step, setStep] = useState<EngagementStep>('handle');
  const [twitterHandle, setTwitterHandle] = useState('');
  const [tweetUrl, setTweetUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<EngagementStep>>(new Set());

  useEffect(() => {
    // For simplified onboarding, we skip checking existing Twitter handle
    // Users will enter their handle fresh each time
  }, [walletAddress]);

  const generateTweet = () => {
    const tweetText = `Joining the BroMentum movement! 🚀 Building a decentralized future with $BRO.\n\n#BroMentum #DeFi #Web3 @BroMentumXYZ`;
    const encodedTweet = encodeURIComponent(tweetText);
    return `https://twitter.com/intent/tweet?text=${encodedTweet}`;
  };

  const handleTweetUrlSubmit = async () => {
    if (!tweetUrl.trim()) {
      toast.error('Please enter your tweet URL');
      return;
    }

    if (!tweetUrl.includes('twitter.com') && !tweetUrl.includes('x.com')) {
      toast.error('Please enter a valid Twitter/X URL');
      return;
    }

    // Extract username from URL to verify it matches the linked handle
    const extractUsername = (url: string) => {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        return pathParts[1]; // Should be the username
      } catch {
        return null;
      }
    };

    const tweetUsername = extractUsername(tweetUrl);
    if (!tweetUsername) {
      toast.error('Unable to extract username from URL');
      return;
    }

    if (tweetUsername.toLowerCase() !== twitterHandle.toLowerCase()) {
      toast.error(`Tweet must be from your linked Twitter account (@${twitterHandle})`);
      return;
    }

    setIsLoading(true);
    try {
      setCompletedSteps(prev => new Set([...prev, 'tweet']));
      setStep('verify');
      toast.success('Tweet URL verified successfully!');
    } catch (error) {
      console.error('Error verifying tweet URL:', error);
      toast.error('Failed to verify tweet URL');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyComplete = () => {
    setCompletedSteps(prev => new Set([...prev, 'verify']));
    onComplete();
  };

  const handleTwitterHandleSubmit = async () => {
    if (!twitterHandle.trim()) {
      toast.error('Please enter your Twitter handle');
      return;
    }

    const cleanHandle = twitterHandle.replace('@', '').trim();
    
    setIsLoading(true);
    try {
      const result = await linkTwitterHandleByWallet(walletAddress, cleanHandle);

      if (result.success) {
        setTwitterHandle(cleanHandle);
        setCompletedSteps(prev => new Set([...prev, 'handle']));
        setStep('tweet');
        toast.success('Twitter handle linked successfully!');
      } else {
        toast.error(result.error || 'Failed to link Twitter handle');
      }
    } catch (error) {
      console.error('Error linking Twitter handle:', error);
      toast.error('Failed to link Twitter handle');
    } finally {
      setIsLoading(false);
    }
  };

  const getStepStatus = (stepName: EngagementStep) => {
    if (completedSteps.has(stepName)) return 'secondary';
    if (step === stepName) return 'default';
    return 'outline';
  };

  const getStepIcon = (stepName: EngagementStep) => {
    if (completedSteps.has(stepName)) return <Check className="mr-2" size={16} />;
    if (step === stepName) return <Twitter className="mr-2" size={16} />;
    return <Twitter className="mr-2" size={16} />;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-tan mb-4">Twitter Engagement</h2>
        <p className="text-tan-dark">Connect your Twitter account to earn more XP!</p>
      </div>

      <div className="flex justify-between">
        <Badge variant={getStepStatus('handle')}>
          {getStepIcon('handle')}
          Link Handle
        </Badge>
        <Badge variant={getStepStatus('tweet')}>
          {getStepIcon('tweet')}
          Post Tweet
        </Badge>
        <Badge variant={getStepStatus('verify')}>
          {getStepIcon('verify')}
          Verify
        </Badge>
      </div>
      
      {step === 'handle' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Twitter className="text-blue-400" size={20} />
              Link Your Twitter Handle
            </CardTitle>
            <CardDescription>
              Enter your Twitter handle to connect your social presence
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="@yourusername"
                value={twitterHandle}
                onChange={(e) => setTwitterHandle(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleTwitterHandleSubmit}
                disabled={isLoading || !twitterHandle.trim()}
              >
                {isLoading ? 'Linking...' : 'Link Handle'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'tweet' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Twitter className="text-blue-400" size={20} />
              Post a Tweet
            </CardTitle>
            <CardDescription>
              Share your support for BroMentum on Twitter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Click the button below to generate a pre-filled tweet. Feel free to customize it!</p>
            <Button asChild>
              <a href={generateTweet()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                <ExternalLink className="mr-2" size={16} />
                Generate Tweet
              </a>
            </Button>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="https://twitter.com/yourusername/status/1234567890"
                value={tweetUrl}
                onChange={(e) => setTweetUrl(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleTweetUrlSubmit}
                disabled={isLoading || !tweetUrl.trim()}
              >
                {isLoading ? 'Verifying...' : 'Verify Tweet'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'verify' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="text-green-400" size={20} />
              Verify Engagement
            </CardTitle>
            <CardDescription>
              Confirm your Twitter engagement to complete this step
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Thank you for supporting BroMentum! Click the button below to complete the verification process.</p>
            <Button onClick={handleVerifyComplete}>
              Complete Verification
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TwitterEngagementStep;
